import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {EMPTY, Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';

import {AuthenticationService} from '../service/auth.service';
import {Router} from "@angular/router";
import {LoggingService} from "../service/logging.service";
import {EventService} from "../service/event.service";
import {upgradeRequest} from '../../shared/limits/limit-failure.model';
import {REQUEST_ID_HEADER} from "../service/action-context.service";

/** Kontrakt s BE — `EmailNotVerifiedAccessDeniedHandler.ERROR_CODE` v aicore. */
const EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(private authenticationService: AuthenticationService,
                private router: Router,
                private logger: LoggingService,
                private eventService: EventService) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(catchError(err => {
            if (err.status === 401) {
                // auto logout if 401 response returned from api
                this.authenticationService.logout(true);
            } else if (err.status === 403 && err.error?.detail === EMAIL_NOT_VERIFIED) {
                // Neověřený účet drží platný token, takže ho BE odmítne až autorizací. Guard by
                // uživatele odklonil až při příští navigaci — do té doby by mu stránka jen tiše
                // přestala fungovat. Marker posílá EmailNotVerifiedAccessDeniedHandler; ostatní
                // 403 (chybějící OAuth scope z IntegrationApi) se takhle chovat nesmí.
                this.authenticationService.logout()
                    .catch(() => undefined)
                    .then(() => this.router.navigate(['/auth/login'], {queryParams: {verifyEmail: true}}));
                return EMPTY;
            } else if (err.status === 402) { // payment required =:-)
                this.logger.warn("Unauthorized request sent. Appropriate subscription is missing.", request, err)
                this.router.navigate(['/402']);
                return EMPTY;
            } else if (err.status === 418) { // I'm a teapot - limit exceeded
                // Broadcast místo přímé závislosti na WSErrorsService/UpgradeModalService — interceptor
                // nesmí (tranzitivně) viset na HttpClient, jinak NG0200 (cyklus přes HTTP_INTERCEPTORS).
                this.eventService.broadcast('show-upgrade-modal', upgradeRequest(err.error, 'http'));
            }

            this.handleError(err, request);
            return throwError(() => err);
        }));
    }

    private async handleError(error: any, request: HttpRequest<unknown>) {
        // Id z hlavičky requestu, ne aktuální — v době chyby už může běžet jiná uživatelská akce
        this.logger.error("Error occurred during API call", error, {requestId: request.headers?.get(REQUEST_ID_HEADER)});
    }
}
